import React from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { SvgIcon } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { ALERT_SYSTEM_TYPE_CONFIG } from '../../config/alertSystem.constants'
import type {
  AlertHoverPayload,
  BirthdayAlertBody,
  CustomAlertBody,
  DaysAbsentAlertBody,
  FirstSessionAlertBody,
  FormsAlertBody,
  IntroOffersAlertBody,
  LocationAlertBody,
  PaymentAlertBody,
} from './alertHover.types'
import type { AlertSystemType } from '../../config/alertSystem.types'

import { AlertHoverBodies } from './AlertHoverBodies'
import { alertHoverStyles } from './AlertHover.styles'
import { getBirthdayMessage } from '@hapana/shared/utils/date.util'

type Props = {
  data: AlertHoverPayload
  onOpenAlerts?: () => void
}

const EXPANDED_TYPES = new Set<AlertSystemType>(['packages', 'customAlert'])

function getInlineInfo(
  data: AlertHoverPayload,
  t: (key: string, options?: Record<string, unknown>) => string
): string | null {
  const { type, body } = data
  switch (type) {
    case 'payment': {
      const b = body as PaymentAlertBody
      const total = (b.failed || 0) + (b.outstanding || 0)
      return total > 0 ? t('hover.totalAlerts', { count: total }) : null
    }
    case 'forms': {
      const b = body as FormsAlertBody
      return b.pendingCount > 0 ? t('hover.formsPending', { count: b.pendingCount }) : null
    }
    case 'location': {
      const b = body as LocationAlertBody
      return b.homeSiteName || null
    }
    case 'birthday': {
      const b = body as BirthdayAlertBody
      if (!b.date) {
        return null
      }
      const result = getBirthdayMessage(b.date, t)
      return result ? result.formatted : b.date
    }
    case 'introOffers': {
      const b = body as IntroOffersAlertBody
      return b?.count > 0 ? t('introOffers.count', { count: b.count }) : null
    }
    case 'daysAbsent': {
      const b = body as DaysAbsentAlertBody
      return t('daysAbsent.body', { count: b.daysAbsent })
    }
    case 'firstSession': {
      const b = body as FirstSessionAlertBody
      return b?.siteName ? t('firstSession.messageAtSite', { siteName: b.siteName }) : null
    }
    case 'destinationFee': {
      return t('destinationFee.body')
    }
    case 'customAlert': {
      const b = body as CustomAlertBody
      return b?.items?.[0]?.title || null
    }
    default:
      return null
  }
}

export const AlertHoverCard: React.FC<Props> = ({ data, onOpenAlerts }) => {
  const { t } = useTranslation('alerts')

  const { type } = data
  const config = ALERT_SYSTEM_TYPE_CONFIG[type]

  if (!config) {
    console.warn(`Alert type config missing for type: ${type}`)
    return null
  }

  const { Icon, colors: configColors } = config
  const colors = data.colorsOverride ?? configColors
  const isExpanded = EXPANDED_TYPES.has(type)
  const inlineInfo = !isExpanded ? getInlineInfo(data, t) : null

  if (isExpanded) {
    return (
      <Box sx={alertHoverStyles.expandedCard}>
        <Box sx={alertHoverStyles.expandedHeader}>
          <Box sx={alertHoverStyles.headerLeft}>
            <Box sx={alertHoverStyles.iconWrapper(colors.bg)}>
              <SvgIcon component={Icon} inheritViewBox sx={{ color: colors.icon }} />
            </Box>
            <Typography sx={alertHoverStyles.title}>{t(`${type}.title`)}</Typography>
          </Box>
          {onOpenAlerts && (
            <Typography sx={alertHoverStyles.openAlertsLink} onClick={onOpenAlerts}>
              {t('hover.openAlerts')}
            </Typography>
          )}
        </Box>
        <AlertHoverBodies data={data} />
      </Box>
    )
  }

  return (
    <Box sx={alertHoverStyles.inlineCard}>
      <Box sx={alertHoverStyles.iconWrapper(colors.bg)}>
        <SvgIcon component={Icon} inheritViewBox sx={{ color: colors.icon }} />
      </Box>
      <Typography sx={alertHoverStyles.title}>{t(`${type}.title`)}</Typography>
      {inlineInfo && <Typography sx={alertHoverStyles.inlineInfo}>{inlineInfo}</Typography>}
      {onOpenAlerts && (
        <Typography sx={alertHoverStyles.openAlertsLink} onClick={onOpenAlerts}>
          {t('hover.openAlerts')}
        </Typography>
      )}
    </Box>
  )
}
